<template>
  <div>
    <h2 class="text-left alert" :class="[removed ? 'alert-danger' : 'alert-success']">
      Trace Elements / PP+
      <a class="btn btn-sm" @click="$emit('onRemoveSection', stepName)">
        <span class="glyphicon" :class="[removed ? 'glyphicon-plus' : 'glyphicon-remove']"></span>
      </a>
    </h2>
    <template v-if="!removed">
      <report-comment field_name="heading_trace_elements_pp" :model="model.COMMENTS" :heading="true"></report-comment>

      <subsection title="Serum MG" :step-name="stepName" subsection="serum_mg">
        <report-comment field_name="heading_serum_mg" :model="model.COMMENTS" :heading="true"></report-comment>

        <table class="table table-bordered">
          <thead>
            <tr>
              <th>Date</th>
              <th>Animal Name</th>
              <th>Description</th>
              <th>Test Type</th>
              <th>avg</th>
              <th># &lt; 0.8</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(t_mg, index) in model.CHARTS.ter_mg_results" :key="index">
              <td class="text-left">{{ t_mg['Date'] }}</td>
              <td class="text-left">{{ t_mg['Animal Name'] }}</td>
              <td class="text-left">{{ t_mg['Description'] }}</td>
              <td class="text-left">{{ t_mg['Test Type'] }}</td>
              <td class="text-left">{{ t_mg['Average'] }}</td>
              <td class="text-left">{{ t_mg['# &lt; 0.8'] }}</td>
            </tr>
          </tbody>
        </table>

        <template v-if="model.CHARTS.ter_mg_results.length > 0">
          <box-plot-chart :chart-data="get_mg_data" ref="serum_mg_chart" />
        </template>
      </subsection>

      <subsection title="Serum SE" :step-name="stepName" subsection="serum_se">
        <report-comment field_name="heading_serum_se" :model="model.COMMENTS" :heading="true"></report-comment>

        <table class="table table-bordered">
          <thead>
            <tr>
              <th>Date</th>
              <th>Animal Name</th>
              <th>Description</th>
              <th>Test Type</th>
              <th>avg</th>
              <th># &lt; 500</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(t_se, index) in model.CHARTS.ter_se_results" :key="index">
              <td class="text-left">{{ t_se['Date'] }}</td>
              <td class="text-left">{{ t_se['Animal Name'] }}</td>
              <td class="text-left">{{ t_se['Description'] }}</td>
              <td class="text-left">{{ t_se['Test Type'] }}</td>
              <td class="text-left">{{ t_se['Average'] }}</td>
              <td class="text-left">{{ t_se['# &lt; 500'] }}</td>
            </tr>
          </tbody>
        </table>

        <template v-if="model.CHARTS.ter_se_results.length > 0">
          <box-plot-chart :chart-data="get_se_data" ref="serum_se_chart" />
        </template>
      </subsection>

      <subsection title="Liver CU" :step-name="stepName" subsection="liver_cu">
        <report-comment field_name="heading_liver_cu" :model="model.COMMENTS" :heading="true"></report-comment>

        <table class="table table-bordered">
          <thead>
            <tr>
              <th>Date</th>
              <th>Animal Name</th>
              <th>Description</th>
              <th>Test Type</th>
              <th>avg</th>
              <th># &lt; 500</th>
              <th># &gt; 3000</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(t_cu, index) in model.CHARTS.ter_cu_results" :key="index">
              <td class="text-left">{{ t_cu['Date'] }}</td>
              <td class="text-left">{{ t_cu['Animal Name'] }}</td>
              <td class="text-left">{{ t_cu['Description'] }}</td>
              <td class="text-left">{{ t_cu['Test Type'] }}</td>
              <td class="text-left">{{ t_cu['Average'] }}</td>
              <td class="text-left">{{ t_cu['# &lt; 500'] }}</td>
              <td class="text-left">{{ t_cu['# &gt; 3000'] }}</td>
            </tr>
          </tbody>
        </table>

        <template v-if="model.CHARTS.ter_cu_results.length > 0">
          <box-plot-chart :chart-data="get_cu_data" ref="liver_cu_chart" />
        </template>
      </subsection>

      <subsection
        :title="farm.farm_label"
        :step-name="stepName"
        :subsection="`farm_${farm.id}-extra_fields`"
        v-for="farm in model.FARMS"
        :key="farm.id"
      >
        <dl class="dl-horizontal">
          <dt>Continue PP+</dt>
          <dd>{{ model[`farm_${farm.id}-continue_ppp`] || '-' }}</dd>

          <dt>Join PP+</dt>
          <dd>{{ model[`farm_${farm.id}-join_ppp`] || '-' }}</dd>

          <dt>Custom Mineral Mix?</dt>
          <dd>{{ model[`farm_${farm.id}-te_ppp_custom_min_mix`] || '-' }}</dd>
          <report-comment
            :field_name="`farm_${farm.id}-te_ppp_custom_min_mix`"
            :model="model.COMMENTS"
          ></report-comment>

          <dt>Interested in Rumenox?</dt>
          <dd>{{ model[`farm_${farm.id}-wants_rumenox`] || '-' }}</dd>

          <dt>Do you want an energy watch visit?</dt>
          <dd>{{ model[`farm_${farm.id}-wants_energy_watch_visit`] || '-' }}</dd>
        </dl>
      </subsection>
    </template>
  </div>
</template>

<script>
import BoxPlotChart from '@/components/charts/BoxPlotChart'
import mixin from './mixin'

export default {
  name: 'TraceElements',
  mixins: [mixin],
  components: {
    BoxPlotChart,
  },
  methods: {
    widthForRow(farms) {
      return farms.length * 500
    },
    getLabels(data) {
      return data.map((row) => row['Date'])
    },
    getData(data) {
      return data.map((row) => row['res'].split(', ').map((n) => parseFloat(n)))
    },
  },
  computed: {
    get_mg_data: function () {
      return {
        labels: this.getLabels(this.model.CHARTS.ter_mg_results),
        datasets: [
          {
            label: 'Serum MG',
            borderWidth: 2,
            borderColor: 'rgba(0, 0, 0, .4)',
            backgroundColor: 'rgba(51, 122, 183, 0.6)',
            data: this.getData(this.model.CHARTS.ter_mg_results),
          },
        ],
      }
    },
    get_se_data: function () {
      return {
        labels: this.getLabels(this.model.CHARTS.ter_se_results),
        datasets: [
          {
            label: 'Serum SE',
            borderWidth: 2,
            borderColor: 'rgba(0, 0, 0, .4)',
            backgroundColor: 'rgba(51, 122, 183, 0.6)',
            data: this.getData(this.model.CHARTS.ter_se_results),
          },
        ],
      }
    },
    get_cu_data: function () {
      return {
        labels: this.getLabels(this.model.CHARTS.ter_cu_results),
        datasets: [
          {
            label: 'Liver CU',
            borderWidth: 2,
            borderColor: 'rgba(0, 0, 0, .4)',
            backgroundColor: 'rgba(51, 122, 183, 0.6)',
            data: this.getData(this.model.CHARTS.ter_cu_results),
          },
        ],
      }
    },
  },
}
</script>
